import React, { FC } from 'react';
import { Button, Space } from 'antd';
import { ReactComponent as ApproveIcon } from 'assets/icons/approve.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as MoveOutIcon } from 'assets/icons/move-white.svg';
import { ReactComponent as PrintIcon } from 'assets/icons/print.svg';
import { ReactComponent as RejectIcon } from 'assets/icons/reject.svg';
import { ICitationRead } from 'common/services/citation-service/citation.service.types';
import { getCitationActionAvailability } from '../helpers/citation-utils';
import configurationStore from 'common/stores/configuration/configuration.store';
import { CitationStatus, RestrictionStateStatus } from 'features/eiq-manage/enums/citation-status.enum';

interface CitationActionButtonsProps {
  citation?: ICitationRead | null;
  isConfirmPaymentEnabled: boolean;
  onPrint?: () => void;
  onEdit?: () => void;
  onMove?: () => void;
  onConfirm?: () => void;
  onApprove?: () => void;
  onConfirmPayment?: () => void;
  onWaive?: () => void;
  onCancel?: () => void;
  onExportPdf?: () => void;
  onSuspendTransponders?: () => void;
  onUnsuspendTransponders?: () => void;
}

const CitationActionButtons: FC<CitationActionButtonsProps> = ({
  citation,
  isConfirmPaymentEnabled,
  onPrint,
  onEdit,
  onMove,
  onConfirm,
  onApprove,
  onConfirmPayment,
  onWaive,
  onCancel,
  onExportPdf,
  onSuspendTransponders,
  onUnsuspendTransponders,
}) => {
  const availability = getCitationActionAvailability(citation);
  const isAutoSuspendTransponders = !!configurationStore.configuration?.citations.isAutoSuspendTransponders;
  const showSuspendTranspondersButton =
    citation?.status !== CitationStatus.Paid && citation?.status !== CitationStatus.Waived && citation?.status !== CitationStatus.Canceled;

  return (
    <Space size={8} wrap>
      {onExportPdf && (
        <Button type="primary" size="middle" onClick={onExportPdf} className="mobile-btn" disabled={!citation}>
          Export PDF
        </Button>
      )}
      {onPrint && (
        <Button type="default" icon={<PrintIcon />} size="middle" onClick={onPrint} className="mobile-btn" disabled={!citation}>
          Print
        </Button>
      )}
      {onEdit && (
        <Button
          type="default"
          icon={<EditIcon />}
          className="mobile-btn"
          size="middle"
          onClick={onEdit}
          disabled={availability.isEditDisabled}>
          Edit
        </Button>
      )}
      {onMove && (
        <Button
          type="primary"
          className="mobile-btn"
          size="middle"
          icon={<MoveOutIcon />}
          onClick={onMove}
          disabled={availability.isMoveDisabled}>
          Move
        </Button>
      )}
      {onConfirm && (
        <Button size="middle" type="primary" onClick={onConfirm} disabled={availability.isConfirmDisabled}>
          Confirm
        </Button>
      )}
      {onApprove && (
        <Button
          icon={<ApproveIcon />}
          size="middle"
          type="primary"
          onClick={onApprove}
          disabled={availability.isApproveDisabled}
          className="mobile-btn">
          Approve
        </Button>
      )}
      {onConfirmPayment && isConfirmPaymentEnabled && (
        <Button
          icon={<ApproveIcon />}
          className="mobile-btn"
          size="middle"
          type="primary"
          onClick={onConfirmPayment}
          disabled={availability.isConfirmPaymentDisabled}>
          Confirm payment
        </Button>
      )}
      {onSuspendTransponders &&
        !isAutoSuspendTransponders &&
        citation?.restrictionStateStatus === RestrictionStateStatus.Paused &&
        showSuspendTranspondersButton && (
          <Button
            className="mobile-btn"
            size="middle"
            type="primary"
            onClick={onSuspendTransponders}
            disabled={availability.isSuspendTransponderDisabled}>
            {`Suspend Transponders ${citation.totalRemainingDays ? `(Total ${citation.totalRemainingDays} days)` : ''}`}
          </Button>
        )}
      {onUnsuspendTransponders && !isAutoSuspendTransponders && citation?.restrictionStateStatus !== RestrictionStateStatus.Paused && (
        <Button
          className="mobile-btn"
          size="middle"
          type="primary"
          onClick={onUnsuspendTransponders}
          disabled={availability.isUnsaspendTransponderDisabled}>
          Unsuspend Transponders
        </Button>
      )}
      {onWaive && (
        <Button className="mobile-btn" size="middle" type="primary" onClick={onWaive} danger disabled={availability.isCancelDisabled}>
          Waive
        </Button>
      )}
      {onCancel && (
        <Button
          icon={<RejectIcon />}
          className="mobile-btn"
          size="middle"
          type="primary"
          onClick={onCancel}
          danger
          disabled={availability.isCancelDisabled}>
          Cancel
        </Button>
      )}
    </Space>
  );
};

export default CitationActionButtons;
